import { Link, useNavigate } from "@tanstack/react-router";
import { useState, type FormEvent } from "react";

import { HEARD_FROM, captureAttribution, type HeardFrom } from "../auth/attribution";
import { useSession } from "../auth/session";
import { Button, Card, ErrorNote, Field, Input, Select } from "../ui/primitives";

/** Mirrors the API's floor, so the failure is shown before the round trip. */
const MIN_PASSWORD = 8;

export function RegisterPage() {
  const session = useSession();
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const [heardFrom, setHeardFrom] = useState<HeardFrom | "">("");
  // Read once: the tags this visit arrived with do not change while the form
  // is open, and re-reading on every keystroke would touch storage for nothing.
  const [attribution] = useState(captureAttribution);

  const tooShort = password.length > 0 && password.length < MIN_PASSWORD;

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (tooShort) return;

    setError(null);
    setBusy(true);
    try {
      await session.signUp(email, password, {
        ...attribution,
        ...(heardFrom ? { heard_from: heardFrom } : {}),
      });
      // Straight into the wizard: an empty dashboard is not a useful landing
      // place, and connecting a store is the only thing worth doing first.
      await navigate({ to: "/sites" });
    } catch (caught) {
      setError(caught);
    } finally {
      setBusy(false);
    }
  }

  return (
    <main id="main" className="mx-auto flex min-h-dvh max-w-sm flex-col justify-center px-6">
      <h1 className="mb-1 text-xl font-semibold">Create your account</h1>
      <p className="mb-6 text-sm text-muted">
        You will connect your store in the next step.
      </p>

      <Card>
        <form onSubmit={submit} className="space-y-4">
          {error != null && <ErrorNote error={error} />}

          <Field label="Email" id="email">
            <Input
              id="email"
              type="email"
              autoComplete="username"
              required
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
          </Field>

          <Field
            label="Password"
            id="password"
            hint={`At least ${MIN_PASSWORD} characters.`}
            error={tooShort ? `Use at least ${MIN_PASSWORD} characters.` : null}
          >
            <Input
              id="password"
              type="password"
              autoComplete="new-password"
              required
              minLength={MIN_PASSWORD}
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
          </Field>

          {/* Optional and last, so it never stands between someone and an
              account. It is the only way to count the channels tracking cannot
              see: word of mouth, a forum thread, an AI assistant's answer. */}
          <Field label="How did you hear about us? (optional)" id="heard-from">
            <Select
              id="heard-from"
              value={heardFrom}
              onChange={(event) => setHeardFrom(event.target.value as HeardFrom | "")}
            >
              <option value="">Choose one</option>
              {HEARD_FROM.map((answer) => (
                <option key={answer.value} value={answer.value}>
                  {answer.label}
                </option>
              ))}
            </Select>
          </Field>

          <Button
            type="submit"
            variant="primary"
            className="w-full"
            disabled={busy || tooShort}
          >
            {busy ? "Creating…" : "Create account"}
          </Button>
        </form>
      </Card>

      <p className="mt-4 text-center text-sm text-muted">
        Already registered?{" "}
        <Link to="/login" className="text-brand underline">
          Sign in
        </Link>
      </p>
    </main>
  );
}
